/**
 * The live map's sums (ADR-021 sections 1 and 6, #598; Figma page 11 "Roster · Live map details", frames M1 to M6, #596).
 *
 * Everything here is plain arithmetic: no React, no Leaflet, no browser. `LiveMap.tsx` asks these functions what to draw
 * and where to move, and Leaflet only draws the background and carries out the move. So the letters, which pins share
 * one numbered pin, and where the map goes when a place is chosen can all be tested without a map on screen.
 *
 * Nothing here is about a person. A position is a place's position from the app's own list (`@vigeo/shared`).
 */

export interface LiveMapPosition {
  readonly lat: number;
  readonly lng: number;
}

/** Where the map is looking: its middle and its zoom level (a whole number; one more is twice as close). */
export interface LiveMapView {
  readonly center: LiveMapPosition;
  readonly zoom: number;
}

/**
 * THE OUTLINE OF A PLACE (Julio, 2026-10-09, #598: coverage is shown as shaded areas). `polygons` is exactly the
 * `coordinates` of a GeoJSON `MultiPolygon`: one or more separate parts; each part is its outer ring followed by any
 * holes; a ring is a closed line of corners, each `[longitude, latitude]`, EAST FIRST. One GeoJSON `Polygon` is
 * `{ polygons: [coordinates] }`.
 *
 * It is the shape `loadVirginiaPlaceOutlines` of `@vigeo/shared` gives (#615), so one of its answers can be handed
 * over as it is. The type is written here, and not taken from there, so the map knows nothing of where outlines come from.
 */
export interface LiveMapOutline {
  readonly polygons: readonly (readonly (readonly (readonly [number, number])[])[])[];
}

/** The smallest upright box around an outline, in degrees. */
export interface LiveMapBox {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

/** The map box on screen, in CSS pixels. */
export interface LiveMapSize {
  readonly width: number;
  readonly height: number;
}

/** The furthest out the map goes: the whole of Virginia fits in the Coverage card at this level. */
export const LIVE_MAP_MIN_ZOOM = 6;
/** The closest the map goes ("the closest zoom" of frame M6, step 3). */
export const LIVE_MAP_MAX_ZOOM = 15;
/** "Town level": where the map stops when a place is chosen, and the closest "Fit all places" ever goes. */
export const LIVE_MAP_TOWN_ZOOM = 11;
/**
 * The closest the map goes when it fits ONE chosen area. A small town's outline would fit at street level, where the
 * map shows nothing HR can place it by.
 */
export const LIVE_MAP_AREA_ZOOM = 13;
/**
 * Two places whose pins would sit closer than this on screen share one numbered pin (frame M6, step 1). A pin is 28
 * across, so at this distance two pins would touch or overlap.
 */
export const LIVE_MAP_GROUP_DISTANCE = 30;
/**
 * The same for the LETTERS OF TWO AREAS (#674; page 12, frame M3 and "M3 detail", approved by Julio on 2026-10-12, #648):
 * closer than this and they share one numbered button. A letter is 20 across. On a touch screen it is pressed with a
 * finger, so letters are kept further apart there.
 */
export const LIVE_MAP_LETTER_DISTANCE = { pointer: 24, touch: 34 } as const;
/**
 * Room kept clear when the map is fitted, measured to the MIDDLE of a pin (a pin is 28 across; the chosen one is 40).
 * Top: the buttons end 44 down, and a pin under a button could not be pressed. Bottom: the credit line. Sides: half of
 * the widest pin and a little more.
 */
export const LIVE_MAP_FIT_PADDING = { top: 58, right: 24, bottom: 32, left: 24 } as const;

/** Room kept clear inside the map box, in CSS pixels from each edge. */
export interface LiveMapPadding {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}
/**
 * Room kept between the edge of an AREA and the edge of the map when every place is fitted. An outline is only a
 * line, so it may come closer to the edge than a button may. Bottom: the credit line's row.
 */
export const LIVE_MAP_AREA_PADDING: LiveMapPadding = { top: 12, right: 12, bottom: 28, left: 12 };
/**
 * The same for the ONE chosen area. The top keeps more, so its dark outline starts clear of the row of buttons, which
 * ends 44 down (56 on a touch screen).
 */
export const LIVE_MAP_CHOSEN_AREA_PADDING: LiveMapPadding = { top: 76, right: 12, bottom: 28, left: 12 };
/**
 * How far beyond the map's box the AREAS are drawn, as a share of the box's own width and height on each side (the
 * `padding` of Leaflet's renderer). Leaflet draws its shapes again only when a move ends, so while the map is dragged
 * the shading reaches only this far. At Leaflet's own 0.1, a drag of more than a tenth of the box showed ground with
 * no shading until the mouse was let go (#759). At 1, a drag of up to the box's whole width or height stays shaded.
 * The drawing is then three times as wide and as high; it holds only the roster's areas, and is drawn once per move.
 */
export const LIVE_MAP_AREAS_DRAWN_BEYOND = 1;
/** How high a button's label is, and the gap kept between it and the button, in CSS pixels. */
export const LIVE_MAP_LABEL = { height: 24, gap: 4 } as const;
/** How far down the map box the row of buttons ends on a touch screen, where it is tallest (44 with a mouse). */
export const LIVE_MAP_BUTTON_ROW = 56;

/**
 * WHERE THE LABEL OF A BUTTON GOES ("Alexandria · 3 people" on an area, the line of words on a numbered button), up
 * or down from the button, in CSS pixels. Just above the button, as frame M2 of page 12 draws it; just below when the
 * button is so near the top that the label would lie under the row of buttons. `anchor` is how far down the map box
 * the middle of the button is, `across` how wide the button is at its biggest (an area's letter is 28 with its dark
 * ring); `by` is from the middle of the button to the label's nearer edge.
 */
export function labelSpot(anchor: number, across = 28): { readonly side: 'above' | 'below'; readonly by: number } {
  const by = across / 2 + LIVE_MAP_LABEL.gap;
  return { side: anchor - by - LIVE_MAP_LABEL.height >= LIVE_MAP_BUTTON_ROW ? 'above' : 'below', by };
}

/**
 * The least box, in CSS pixels, in which the legend's card goes inside the map. In a smaller one the card would cover
 * too much of it: across the whole state, places would lie under the card with no room to move them clear.
 */
export const LIVE_MAP_LEGEND_INSIDE: LiveMapSize = { width: 480, height: 480 };

/**
 * WHETHER THE BOX THE MAP WAS GIVEN HAS ROOM FOR THE LEGEND'S CARD INSIDE THE MAP. A box whose size is not known (it
 * has not been measured, or measures nothing) counts as having room.
 */
export function legendFitsInside(box: LiveMapSize | null): boolean {
  if (box === null || box.width === 0 || box.height === 0) return true;
  return box.width >= LIVE_MAP_LEGEND_INSIDE.width && box.height >= LIVE_MAP_LEGEND_INSIDE.height;
}

/**
 * THE ROOM A FIT KEEPS CLEAR FOR A BUTTON when the legend's card lies over the bottom of the map, as it does on a
 * desktop. `covered` is how far up from the map's bottom edge the card reaches. A button under it could not be
 * pressed, so the fit keeps the middle of every button above it.
 */
export function fitPaddingOver(covered: number): LiveMapPadding {
  return { ...LIVE_MAP_FIT_PADDING, bottom: Math.max(LIVE_MAP_FIT_PADDING.bottom, covered + LIVE_MAP_FIT_PADDING.left) };
}

/**
 * The top right corner, where the + and − buttons are (they end 44 in from the right and 80 down), with half a pin
 * added. A pin whose middle is in here would be partly under a button.
 */
export const LIVE_MAP_BUTTON_CORNER = { width: 64, height: 96 } as const;

/**
 * The credit line, always on the map, as OpenStreetMap's attribution guidelines ask (ADR-023 section 6): "© OpenStreetMap"
 * in a corner, as a link to where the data's licence is.
 */
export const LIVE_MAP_CREDIT = '© OpenStreetMap';
export const LIVE_MAP_CREDIT_LINK = 'https://www.openstreetmap.org/copyright';
/**
 * Who the outlines of the areas come from. The Census Bureau asks that it be named as the source of its boundary
 * files (#615), and a note inside the data does not reach HR's screen, so the credit line names it.
 */
export const LIVE_MAP_AREAS_SOURCE = 'U.S. Census Bureau';
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * The letter of the n-th place (counting from 0): A to Z, then AA, AB ... AZ, BA ... ZZ, AAA and so on. There is no
 * 26-place limit (ADR-021 section 6).
 */
export function liveMapLetter(index: number): string {
  if (!Number.isInteger(index) || index < 0) throw new RangeError(`A pin's place in the order must be a whole number from 0, and it is ${String(index)}.`);
  let letters = '';
  for (let n = index; n >= 0; n = Math.floor(n / ALPHABET.length) - 1) letters = ALPHABET[n % ALPHABET.length]! + letters;
  return letters;
}

/** The letter of each place, given the places in the order of the list (`letterOrder`). Every place gets one. */
export function liveMapLetters(ordered: readonly string[]): Map<string, string> {
  return new Map(ordered.map((code, index) => [code, liveMapLetter(index)] as const));
}

interface Pixel {
  readonly x: number;
  readonly y: number;
}

const WORLD = 256;
const MAX_LATITUDE = 85.0511287798;

/**
 * The box around an outline, or null when the outline has no usable corner. Such an outline counts as no outline:
 * the place is shown as a pin.
 */
export function outlineBox(outline: LiveMapOutline | null | undefined): LiveMapBox | null {
  let box: LiveMapBox | null = null;
  for (const part of outline?.polygons ?? []) for (const ring of part) for (const [lng, lat] of ring) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    box = box === null ? { south: lat, west: lng, north: lat, east: lng } : { south: Math.min(box.south, lat), west: Math.min(box.west, lng), north: Math.max(box.north, lat), east: Math.max(box.east, lng) };
  }
  return box;
}

/** The two opposite corners of a box. On this map a box is in view exactly when these two are. */
export const boxCorners = (box: LiveMapBox): readonly LiveMapPosition[] => [{ lat: box.south, lng: box.west }, { lat: box.north, lng: box.east }];

/**
 * WHERE A PLACE'S BUTTON GOES: its position from the app's own list; without one, the middle of its outline's box;
 * with neither, nowhere (null), and the place is not on the map.
 */
export function anchorOf(place: { readonly position: LiveMapPosition | null; readonly outline?: LiveMapOutline | null | undefined }): LiveMapPosition | null {
  if (place.position !== null) return place.position;
  const box = outlineBox(place.outline);
  return box === null ? null : { lat: (box.south + box.north) / 2, lng: (box.west + box.east) / 2 };
}

/**
 * HOW DARK AN AREA IS SHADED, in steps: 0 is no shading at all (nobody there matches the filters), then one step for
 * each entry of this list the number of staff reaches. Three steps: 1 person, 2 people, 3 or more. Change the list to
 * change the steps (three are drawn at most). Shading is never the only sign: the count is in words on the area's
 * button, on its label and in the legend.
 */
export const LIVE_MAP_SHADE_FROM = [1, 2, 3] as const;
/** The legend's words for each step, lightest first. They say what `LIVE_MAP_SHADE_FROM` says. */
export const LIVE_MAP_SHADE_WORDS = ['1 person', '2 people', '3 or more'] as const;
/** The legend's words for an area with no shading. */
export const LIVE_MAP_SHADE_NOBODY = 'Nobody matches the filters';

/** The step an area is shaded at. A place that matches with no number given gets the lightest step. */
export function shadeStep(matches: boolean, staff: number | undefined): 0 | 1 | 2 | 3 {
  if (!matches) return 0;
  const reached = LIVE_MAP_SHADE_FROM.filter((from) => (staff ?? 0) >= from).length;
  return reached >= 3 ? 3 : reached === 2 ? 2 : 1;
}

/**
 * THE ORDER AREAS ARE DRAWN IN: the largest box first, so a small area that lies inside a large one (a ZIP code area
 * inside its city) is drawn over it and takes the click. Areas of the same size keep the order given.
 */
export function drawOrder<A extends { readonly box: LiveMapBox }>(areas: readonly A[]): A[] {
  const size = (box: LiveMapBox) => (box.north - box.south) * (box.east - box.west);
  return areas.map((area, index) => ({ area, index })).sort((a, b) => size(b.area.box) - size(a.area.box) || a.index - b.index).map((entry) => entry.area);
}

/** A position as pixels of the whole world's map at a zoom level (Web Mercator, the same sum Leaflet uses). */
export function project(position: LiveMapPosition, zoom: number): Pixel {
  const scale = WORLD * 2 ** zoom;
  const sin = Math.sin((Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, position.lat)) * Math.PI) / 180);
  return { x: ((position.lng + 180) / 360) * scale, y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale };
}

/** The other way round: the position of a pixel of the whole world's map. */
export function unproject(pixel: Pixel, zoom: number): LiveMapPosition {
  const scale = WORLD * 2 ** zoom;
  const n = Math.PI - (2 * Math.PI * pixel.y) / scale;
  return { lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))), lng: (pixel.x / scale) * 360 - 180 };
}

interface Positioned {
  readonly code: string;
  readonly position: LiveMapPosition;
}

/** One thing drawn on the map: a place's own pin, or one numbered pin that stands for several places. */
export type LiveMapPin<P extends Positioned> =
  | { readonly kind: 'place'; readonly key: string; readonly position: LiveMapPosition; readonly place: P }
  | { readonly kind: 'group'; readonly key: string; readonly position: LiveMapPosition; readonly places: readonly P[] };

/** How close two places' buttons may come before they share one: one number for every pair, or a sum that looks at the pair. */
export type LiveMapDistance<P> = number | ((one: P, other: P) => number);

/**
 * HOW CLOSE THE BUTTONS OF TWO PLACES MAY COME on this map (#674). Each kind of button has a distance of its own: a
 * pin `LIVE_MAP_GROUP_DISTANCE`, an area's letter `LIVE_MAP_LETTER_DISTANCE` (more on a touch screen). Two of a kind
 * are held to that distance; a letter beside a pin to half of each. A place with a box is an area, without one a pin.
 */
export function buttonDistance(touch: boolean): (one: { readonly box?: LiveMapBox | null | undefined }, other: { readonly box?: LiveMapBox | null | undefined }) => number {
  const half = (place: { readonly box?: LiveMapBox | null | undefined }) => (place.box ? LIVE_MAP_LETTER_DISTANCE[touch ? 'touch' : 'pointer'] : LIVE_MAP_GROUP_DISTANCE) / 2;
  return (one, other) => half(one) + half(other);
}

/**
 * WHICH PLACES SHARE A BUTTON at a zoom level (frame M6, step 1, for pins; page 12, frame M3, for the letters of areas).
 *
 * Two places whose buttons would be closer than `distance` on screen share one. So do places joined through others:
 * if the first is too close to the second and the second to the third, all three share one, however far the first is
 * from the third. So no two buttons that are drawn are ever closer than `distance` allows, except a numbered one,
 * which stands in the middle of its places. A place by itself keeps its own lettered button. The answer is in the
 * order given (the order of the letters), a numbered button standing where its first place would.
 *
 * A place named in `alone` never shares a button. The chosen place is passed there, so its ring and its name are always
 * on screen. (Not drawn in the frames and not decided by Julio: #598, "a numbered pin containing the chosen place".)
 *
 * The answer depends only on the places, the zoom, `alone` and `distance`: not on where the map has been dragged to.
 */
export function groupPins<P extends Positioned>(places: readonly P[], zoom: number, alone: readonly string[] = [], distance: LiveMapDistance<P> = LIVE_MAP_GROUP_DISTANCE): LiveMapPin<P>[] {
  const at = places.map((place) => project(place.position, zoom));
  const single = places.map((place) => alone.includes(place.code));
  const allowed = typeof distance === 'number' ? () => distance : distance;
  // `leader[n]` is an earlier place that place n shares a button with, or n itself. Followed to its end it names the
  // first place of the button, so buttons come out in the order of their first places.
  const leader = places.map((_, index) => index);
  const first = (index: number): number => (leader[index] === index ? index : first(leader[index]!));
  places.forEach((place, index) => {
    if (single[index]) return;
    for (let earlier = 0; earlier < index; earlier += 1) {
      if (single[earlier] || Math.hypot(at[earlier]!.x - at[index]!.x, at[earlier]!.y - at[index]!.y) >= allowed(places[earlier]!, place)) continue;
      const [a, b] = [first(earlier), first(index)];
      leader[Math.max(a, b)] = Math.min(a, b);
    }
  });
  const members = new Map<number, number[]>();
  places.forEach((_, index) => members.set(first(index), [...(members.get(first(index)) ?? []), index]));
  return [...members.values()].map((indexes): LiveMapPin<P> => {
    const sharing = indexes.map((index) => places[index]!);
    const [only] = sharing;
    if (sharing.length === 1) return { kind: 'place', key: only!.code, position: only!.position, place: only! };
    const middle = { x: indexes.reduce((sum, index) => sum + at[index]!.x, 0) / indexes.length, y: indexes.reduce((sum, index) => sum + at[index]!.y, 0) / indexes.length };
    return { kind: 'group', key: `group:${sharing.map((member) => member.code).join('+')}`, position: unproject(middle, zoom), places: sharing };
  });
}

/**
 * How far to zoom in for a numbered button to come apart (frame M6, step 2): the first level past `fromZoom` at which
 * its places no longer all share one button. Null when they still share one at the closest zoom: the button then opens
 * its short list instead (step 3). `distance` must be the one the buttons were grouped with.
 */
export function splitZoom<P extends Positioned>(places: readonly P[], fromZoom: number, maxZoom: number = LIVE_MAP_MAX_ZOOM, distance: LiveMapDistance<P> = LIVE_MAP_GROUP_DISTANCE): number | null {
  for (let zoom = Math.floor(fromZoom) + 1; zoom <= maxZoom; zoom += 1) {
    if (groupPins(places, zoom, [], distance).length > 1) return zoom;
  }
  return null;
}

/**
 * THE VIEW THAT SHOWS EVERY POSITION GIVEN ("Fit all places", and how the map opens): the closest whole zoom level, no
 * closer than town level, at which they all fit in the box with `LIVE_MAP_FIT_PADDING` kept clear. One position alone is
 * shown at town level. Null when there is nothing to show.
 *
 * `corners` are further points that must be in view and are not buttons: the corners of the areas' boxes. They keep
 * to `cornerPadding`, which is less than a button needs, and are free to lie under the + and − buttons.
 *
 * The pins sit in the middle of the room kept clear. If that would put one under the + and − buttons, and there is
 * room to spare, they are moved down, or left, or both, by half of what is spare. If one is still under a button the
 * first placing is kept: the pin can be dragged clear, and the place is always in the places list.
 *
 * `keep` is the room a button keeps to; it is given when something more lies over the map (`fitPaddingOver`).
 */
export function fitView(
  positions: readonly LiveMapPosition[], size: LiveMapSize, maxZoom: number = LIVE_MAP_TOWN_ZOOM,
  corners: readonly LiveMapPosition[] = [], cornerPadding: LiveMapPadding = LIVE_MAP_AREA_PADDING, keep: LiveMapPadding = LIVE_MAP_FIT_PADDING,
): LiveMapView | null {
  if (positions.length === 0 && corners.length === 0) return null;
  for (let zoom = maxZoom; zoom >= LIVE_MAP_MIN_ZOOM; zoom -= 1) {
    const pixels = positions.map((position) => project(position, zoom));
    const points = [...pixels.map((at) => ({ at, keep })), ...corners.map((corner) => ({ at: project(corner, zoom), keep: cornerPadding }))];
    // How far right and down the map box's own top left corner can be, on the world's map, with every point still
    // inside the room it must keep to: anywhere from `from` to `to`. When `from` is past `to` they do not all fit.
    const x = { from: Math.max(...points.map(({ at, keep }) => at.x - size.width + keep.right)), to: Math.min(...points.map(({ at, keep }) => at.x - keep.left)) };
    const y = { from: Math.max(...points.map(({ at, keep }) => at.y - size.height + keep.bottom)), to: Math.min(...points.map(({ at, keep }) => at.y - keep.top)) };
    if (zoom > LIVE_MAP_MIN_ZOOM && (x.from > x.to || y.from > y.to)) continue;
    // The first placing leaves the same to spare on both sides, and above and below.
    // `moved` is how far the pins are moved on screen from there (right and down count up); the view moves the other way.
    const middle = (moved: Pixel): Pixel => ({ x: (x.from + x.to) / 2 + size.width / 2 - moved.x, y: (y.from + y.to) / 2 + size.height / 2 - moved.y });
    const underButtons = (moved: Pixel): boolean => pixels.some((p) => {
      const center = middle(moved);
      return p.x - center.x + size.width / 2 > size.width - LIVE_MAP_BUTTON_CORNER.width && p.y - center.y + size.height / 2 < LIVE_MAP_BUTTON_CORNER.height;
    });
    const spare = { x: Math.max(0, x.to - x.from), y: Math.max(0, y.to - y.from) };
    const placings: Pixel[] = [{ x: 0, y: 0 }, { x: 0, y: spare.y / 2 }, { x: -spare.x / 2, y: 0 }, { x: -spare.x / 2, y: spare.y / 2 }];
    return { center: unproject(middle(placings.find((moved) => !underButtons(moved)) ?? placings[0]!), zoom), zoom };
  }
  return null;
}

/** A place as the fit sees it: where its button is, and the box of its area when it has one. */
interface Fitted extends Positioned {
  readonly box?: LiveMapBox | null | undefined;
}

/**
 * "Fit all places", and how the map opens: every button and the whole of every area in view, no closer than town
 * level. Null when there is no place.
 */
export function fitAll(places: readonly Fitted[], size: LiveMapSize, keep: LiveMapPadding = LIVE_MAP_FIT_PADDING): LiveMapView | null {
  return fitView(places.map((place) => place.position), size, LIVE_MAP_TOWN_ZOOM, places.flatMap((place) => (place.box ? boxCorners(place.box) : [])), LIVE_MAP_AREA_PADDING, keep);
}

/**
 * WHERE THE MAP GOES for a selection (#598, Julio 2026-10-09). One chosen place: an AREA is fitted in the box, no
 * closer than `LIVE_MAP_AREA_ZOOM`; a place with no outline is put in the middle at town level. Nothing chosen: every
 * place in view. Several chosen: those places in view, which is the designer's proposal and is not drawn or decided
 * (#596). A chosen place that is not on the map is left out; if none is, every place is shown.
 */
export function viewFor(places: readonly Fitted[], chosen: readonly string[], size: LiveMapSize, keep: LiveMapPadding = LIVE_MAP_FIT_PADDING): LiveMapView | null {
  const picked = places.filter((place) => chosen.includes(place.code));
  const [only] = picked;
  if (picked.length === 1 && only !== undefined) return only.box ? fitView([only.position], size, LIVE_MAP_AREA_ZOOM, boxCorners(only.box), LIVE_MAP_CHOSEN_AREA_PADDING, keep) : { center: only.position, zoom: LIVE_MAP_TOWN_ZOOM };
  return fitAll(picked.length > 1 ? picked : places, size, keep);
}

/** Which side of its pin the chosen place's name goes: the side with more room (frame M2). */
export const nameSide = (pinX: number, width: number): 'left' | 'right' => (pinX >= width / 2 ? 'left' : 'right');
